import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFiscalStatus, getFiscalConfig } from './features/fiscal/fiscalSlice';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams, useLocation as useRRLocation } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Sales from './pages/Sales';
import SalesHistory from './pages/SalesHistory';
import Reports from './pages/Reports';
import IvaSalesReport from './pages/IvaSalesReport';
import IvaPurchasesReport from './pages/IvaPurchasesReport';
import SalesReport from './pages/SalesReport';
import Categories from './pages/Categories';
import CategoriesManagement from './pages/CategoriesManagement';
import Customers from './pages/Customers';
import CashRegister from './pages/CashRegister';
import Login from './pages/Login';
import Register from './pages/Register';
import RegisterBusiness from './pages/RegisterBusiness';
import BusinessSettings from './pages/BusinessSettings';
import Plans from './pages/Plans';
import FiscalConfig from './pages/FiscalConfig';
import Invoices from './pages/Invoices';
import Suppliers from './pages/Suppliers';
import Purchases from './pages/Purchases';
import StockMovements from './pages/StockMovements';
import Users from './pages/Users';
import CuentasCorrientes from './pages/CuentasCorrientes';
import Quotations from './pages/Quotations';
import Ecommerce from './pages/Ecommerce';
import WooCommerce from './pages/WooCommerce';
import PublicShop from './pages/PublicShop';
import RestaurantSalon from './pages/RestaurantSalon';
import RestaurantPOS from './pages/RestaurantPOS';
import KitchenDisplay from './pages/KitchenDisplay';

import PrivateRoute from './components/PrivateRoute';
import { PERM_KEYS } from './utils/permissions';
import { ToastContainer } from 'react-toastify';
import MobileGuard from './components/MobileGuard';
import 'react-toastify/dist/ReactToastify.css';
import { pageBg } from './styles/ui';
import { getTenantInfo } from './features/tenant/tenantSlice';
import SubscriptionBanner from './components/SubscriptionBanner';
import ErrorBoundary from './components/ErrorBoundary';
import Landing from './pages/Landing';
import WebsiteRedirect from './pages/WebsiteRedirect';
import Settings from './pages/Settings';
import ChatBot from './components/ChatBot';
import { useLocation } from 'react-router-dom';

// Componente que redirige rutas /app/* al path correcto de React
// Esto resuelve el problema de callbacks OAuth que redirigen a /app/ecommerce
function AppPathRedirect() {
  const navigate = useNavigate();
  const params = useParams();
  const rrLocation = useRRLocation();
  useEffect(() => {
    const route = params['*'] || '';
    const search = rrLocation.search || '';
    navigate('/' + route + search, { replace: true });
  }, [navigate, params, rrLocation.search]);
  return null;
}

function AppContent() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { tenant } = useSelector((state) => state.tenant || {});
  const serviceType = tenant?.serviceType || user?.tenantId?.serviceType || 'full_pos';
  const isRestaurant = serviceType === 'restaurant';
  const location = useLocation();
  const isPublicShop = location.pathname.startsWith('/shop/');
  // If admin console opens the frontend with ?forceLogout=1, clear any existing tenant session
  // by calling the backend logout endpoint and then redirect to /login.
  React.useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.location) return;
      const params = new URLSearchParams(window.location.search || '');
      if (params.get('forceLogout') === '1') {
        (async () => {
          try {
            await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
          } catch (e) {
            // ignore network errors — we'll still redirect to login
          }
          // Remove the param from the URL and navigate to login
          params.delete('forceLogout');
          const remaining = params.toString();
          const newUrl = window.location.pathname + (remaining ? ('?' + remaining) : '');
          window.history.replaceState({}, '', newUrl);
          window.location.href = '/login';
        })();
      }
    } catch (e) { /* ignore */ }
  }, []);
  const searchParams = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search) : null;
  const previewLanding = searchParams?.get('landing') === '1';
  const previewWebsite = searchParams?.get('site') === '1';

  // Cargar configuración/estado fiscal al iniciar la app (si hay usuario)
  useEffect(() => {
    if (user) dispatch(getFiscalStatus());
    if (user) dispatch(getFiscalConfig());
    if (user) dispatch(getTenantInfo());
  }, [user, dispatch]);

  // Refresco periódico suave de la info del tenant (1 min) para sincronizar cambios hechos desde la consola admin
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      dispatch(getTenantInfo());
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [user, dispatch]);
  return (
    <div style={pageBg}>
      <ToastContainer position="top-right" autoClose={2500} limit={3} newestOnTop={true} closeOnClick={true} pauseOnHover={false} pauseOnFocusLoss={false} />
      {user && !isPublicShop && <SubscriptionBanner />}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register-business" element={<RegisterBusiness />} />
        <Route path="/shop/:slug" element={<PublicShop />} />

        {/* Landing pública:
              - ?landing=1 => fuerza SPA Landing (aun logueado)
              - ?site=1    => fuerza sitio estático /website (aun logueado)
              - por defecto: si hay usuario => /dashboard; sino => /website
           */}
        <Route
          path="/"
          element={
            previewLanding
              ? <Landing />
              : (
                previewWebsite
                  ? <WebsiteRedirect />
                  : (user ? <Navigate to="/dashboard" replace /> : <WebsiteRedirect />)
              )
          }
        />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/products"
          element={
            <PrivateRoute perm={PERM_KEYS.PRODUCTS_ACCESS}>
              <Products />
            </PrivateRoute>
          }
        />
        <Route
          path="/stock-movements"
          element={
            <PrivateRoute perm={PERM_KEYS.STOCK_MOVEMENTS}>
              <StockMovements />
            </PrivateRoute>
          }
        />
        <Route
          path="/sales"
          element={
            <PrivateRoute perm={PERM_KEYS.SALES_ACCESS}>
              {isRestaurant ? <Navigate to="/salon" replace /> : <Sales />}
            </PrivateRoute>
          }
        />
        <Route
          path="/sales-history"
          element={
            <PrivateRoute perm={PERM_KEYS.SALES_HISTORY}>
              <SalesHistory />
            </PrivateRoute>
          }
        />

        <Route
          path="/reports/iva-sales"
          element={
            <PrivateRoute perm={PERM_KEYS.REPORTS_IVA}>
              <MobileGuard>
                <IvaSalesReport />
              </MobileGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/reports/iva-purchases"
          element={
            <PrivateRoute perm={PERM_KEYS.REPORTS_IVA}>
              <MobileGuard>
                <IvaPurchasesReport />
              </MobileGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/categories"
          element={
            <PrivateRoute perm={PERM_KEYS.PRODUCTS_ACCESS}>
              <Categories />
            </PrivateRoute>
          }
        />
        <Route
          path="/categories-management"
          element={
            <PrivateRoute perm={PERM_KEYS.PRODUCTS_ACCESS}>
              <CategoriesManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <PrivateRoute perm={PERM_KEYS.CUSTOMERS_ACCESS}>
              <Customers />
            </PrivateRoute>
          }
        />
        <Route
          path="/cuentas-corrientes"
          element={
            <PrivateRoute perm={PERM_KEYS.CUSTOMERS_ACCESS}>
              <CuentasCorrientes />
            </PrivateRoute>
          }
        />
        <Route
          path="/cash-register"
          element={
            <PrivateRoute perm={PERM_KEYS.CASH_ACCESS}>
              <MobileGuard>
                <CashRegister />
              </MobileGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/business-settings"
          element={
            <PrivateRoute perm={PERM_KEYS.SETTINGS_BUSINESS}>
              <MobileGuard>
                <BusinessSettings />
              </MobileGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/plans"
          element={
            <PrivateRoute roles={['owner', 'admin']}>
              <MobileGuard>
                <Plans />
              </MobileGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/fiscal-config"
          element={
            <PrivateRoute perm={PERM_KEYS.FISCAL_ACCESS}>
              <MobileGuard>
                <FiscalConfig />
              </MobileGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <PrivateRoute perm={PERM_KEYS.INVOICE_ACCESS}>
              <Invoices />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <PrivateRoute perm={PERM_KEYS.SETTINGS_ACCESS}>
              <MobileGuard>
                <Settings />
              </MobileGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/presupuestos"
          element={
            <PrivateRoute perm={PERM_KEYS.QUOTATIONS_ACCESS}>
              {isRestaurant ? <Navigate to="/salon" replace /> : <Quotations />}
            </PrivateRoute>
          }
        />
        <Route
          path="/suppliers"
          element={
            <PrivateRoute perm={PERM_KEYS.SUPPLIERS_ACCESS}>
              <Suppliers />
            </PrivateRoute>
          }
        />
        <Route
          path="/purchases"
          element={
            <PrivateRoute perm={PERM_KEYS.PURCHASES_ACCESS}>
              <MobileGuard>
                <Purchases />
              </MobileGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/users"
          element={
            <PrivateRoute perm={PERM_KEYS.USERS_MANAGE} roles={['owner', 'admin']}>
              <MobileGuard>
                <Users />
              </MobileGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <PrivateRoute perm={PERM_KEYS.REPORTS_ACCESS}>
              <MobileGuard>
                <Reports />
              </MobileGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/reports/sales"
          element={
            <PrivateRoute perm={PERM_KEYS.REPORTS_ACCESS}>
              <MobileGuard>
                <SalesReport />
              </MobileGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/ecommerce"
          element={
            <PrivateRoute roles={['owner', 'admin']}>
              <MobileGuard>
                {isRestaurant ? <Navigate to="/salon" replace /> : <Ecommerce />}
              </MobileGuard>
            </PrivateRoute>
          }
        />
        <Route
          path="/woocommerce"
          element={
            <PrivateRoute roles={['owner', 'admin']}>
              <MobileGuard>
                {isRestaurant ? <Navigate to="/salon" replace /> : <WooCommerce />}
              </MobileGuard>
            </PrivateRoute>
          }
        />
        {/* Rutas para el Modo Restaurante */}
        <Route
          path="/salon"
          element={
            <PrivateRoute>
              <RestaurantSalon />
            </PrivateRoute>
          }
        />
        <Route
          path="/restaurant-pos/:tableId"
          element={
            <PrivateRoute>
              <RestaurantPOS />
            </PrivateRoute>
          }
        />
        <Route
          path="/kitchen"
          element={
            <PrivateRoute>
              <KitchenDisplay />
            </PrivateRoute>
          }
        />
        {/* Captura rutas /app/* generadas por callbacks OAuth y redirige al path React correcto */}
        <Route path="/app/*" element={<AppPathRedirect />} />
      </Routes>
      {!isPublicShop && <ChatBot />}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AppContent />
      </Router>
    </ErrorBoundary>
  );
}

export default App;


